import { CaretRight } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import type { Collection, Document } from "~/api/types";
import { useDocumentAncestors } from "./useDocumentAncestors";

const LINK = "truncate rounded-sm hover:text-text hover:underline focus-visible:outline-accent";

export function DocumentBreadcrumbs({
  document,
  collection,
}: {
  document: Document;
  collection: Collection | undefined;
}) {
  const { t } = useTranslation();
  const { data: ancestors } = useDocumentAncestors(document);
  return (
    <nav aria-label={t("Breadcrumbs")} className="min-w-0 text-small text-text-tertiary">
      <ol className="flex flex-wrap items-center gap-x-1.5 gap-y-1">
        <li className="shrink-0">
          <Link to="/documents" className={LINK}>
            {t("Documents")}
          </Link>
        </li>
        {collection ? (
          <li className="flex min-w-0 max-w-52 items-center gap-1.5">
            <CaretRight size={12} aria-hidden className="shrink-0 rtl:rotate-180" />
            <Link
              to="/collections/$collectionId"
              params={{ collectionId: collection.id }}
              className={LINK}
              title={collection.name}
            >
              {collection.name}
            </Link>
          </li>
        ) : null}
        {(ancestors ?? []).map((parent) => (
          <li key={parent.id} className="flex min-w-0 max-w-52 items-center gap-1.5">
            <CaretRight size={12} aria-hidden className="shrink-0 rtl:rotate-180" />
            <Link
              to="/documents/$documentId"
              params={{ documentId: parent.id }}
              className={LINK}
              title={parent.title}
            >
              {parent.title}
            </Link>
          </li>
        ))}
        <li className="flex min-w-0 max-w-64 items-center gap-1.5">
          <CaretRight size={12} aria-hidden className="shrink-0 rtl:rotate-180" />
          <span aria-current="page" className="truncate text-text-secondary" title={document.title}>
            {document.title}
          </span>
        </li>
      </ol>
    </nav>
  );
}
